/**
 * AppointmentCalendar — Shared scheduling visual component (v152.0.8)
 * 
 * IMPORTANT: This is a VISUAL component only. It does NOT change any flow,
 * screen order, or navigation logic. Each parent screen still manages its own
 * state, API calls, and progression. This component simply renders the calendar
 * grid + time slots and calls back via props when the user picks date/time.
 * 
 * Used in:
 *   - ShareConversation.jsx (initial consult scheduling)
 *   - DeliverScreen.jsx (follow-up reopen scheduling)
 *   - ScheduleFollowupScreen.jsx (follow-up scheduling from encounter)
 * 
 * Props:
 *   availableDates - string[] of 'YYYY-MM-DD' dates with availability
 *   fullyBookedDates - string[] of 'YYYY-MM-DD' dates fully booked (optional)
 *   timeSlots - string[] of time strings (e.g., ['9:00 AM', '10:00 AM'])
 *   selectedDate - string|null currently selected date
 *   selectedTime - string|null currently selected time
 *   onSelectDate - (dateStr) => void
 *   onSelectTime - (timeStr) => void
 *   suggestedRange - { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD' } optional highlight band
 *   timeSubLabels - { [time]: string } optional line under a time button (D7 PD7-1: the
 *                   location name when the physician has more than one; utils/bookableSlots.js
 *                   slotPlaceNames). Absent or null → the buttons render exactly as before.
 */
import { useState, useMemo, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useI18n } from '../i18n'
import { formatCalendarMonth, calendarStripLabel } from '../utils/dateUtils'
import { appLocale } from '../utils/appLanguage'

const BLUE = '#46A8F9'
const NAVY = '#15354F'
const ICE = '#E9F8FF'

// ★★ TEST 3.16 (patient push 3a): an AVAILABLE day is white with a light border and navy text.
//   It was a light-blue fill (rgba(70,168,249,0.10)), which read as "taken / booked" (Alex,
//   2026-09-30). The suggested range keeps a subtle tint so it stays distinguishable; booked
//   (grey + strike-through), unavailable / past (grey text) and selected (solid blue) are
//   unchanged. The legend's "Available" swatch is the same style. The one rule lives here, so
//   the day cells and the legend cannot disagree. scripts/check-calendar-available-style.mjs.
const AVAILABLE_BORDER = '1.5px solid #D8E6F0'
export const AVAILABLE_SWATCH = { background: '#FFFFFF', border: AVAILABLE_BORDER }
export const SUGGESTED_SWATCH = { background: 'rgba(70,168,249,0.10)', border: AVAILABLE_BORDER }

export function dayCellStyle({ isSel, isBooked, isPast, isAvail, inRange }) {
  if (isSel) return { bgColor: BLUE, textColor: 'white', border: 'none', fontWeight: '700', textDecoration: 'none' }
  if (isBooked) return { bgColor: '#F1F5F9', textColor: '#cbd5e1', border: 'none', fontWeight: '500', textDecoration: 'line-through' }
  if (isPast || !isAvail) return { bgColor: 'transparent', textColor: '#cbd5e1', border: 'none', fontWeight: '500', textDecoration: 'none' }
  const swatch = inRange ? SUGGESTED_SWATCH : AVAILABLE_SWATCH
  return { bgColor: swatch.background, textColor: NAVY, border: swatch.border, fontWeight: '600', textDecoration: 'none' }
}
const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

export default function AppointmentCalendar({
  availableDates = [],
  fullyBookedDates = [],
  timeSlots = [],
  selectedDate,
  selectedTime,
  onSelectDate,
  onSelectTime,
  suggestedRange,
  slotCountByDate = {},
  timeSubLabels = null,
}) {
  // v160.1: Use local date, not UTC — toISOString() returns UTC which can be a day ahead
  const { t, language } = useI18n()
  const today = useMemo(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }, [])
  const availableSet = useMemo(() => new Set(availableDates), [availableDates])
  const bookedSet = useMemo(() => new Set(fullyBookedDates), [fullyBookedDates])

  // First available date for highlight
  // v237.18.0: include today (>= today) so same-day booking auto-selects when the
  // doctor has slots today. Consumers apply their own same-day lead-time buffer to
  // the time list, so an empty today simply shows "no times" and the patient can pick ahead.
  const firstAvailable = useMemo(() => {
    const sorted = [...availableDates].sort()
    return sorted.find(d => d >= today && !bookedSet.has(d)) || null
  }, [availableDates, today, bookedSet])

  // Calendar month state — start on the month of the first available date
  const initDate = firstAvailable ? new Date(firstAvailable + 'T12:00:00') : new Date()
  const [viewMonth, setViewMonth] = useState(initDate.getMonth())
  const [viewYear, setViewYear] = useState(initDate.getFullYear())

  // v214.4: Two-week strip is the default view; user can expand to full month.
  // weekOffset advances the strip in 7-day increments without leaving strip mode.
  const [viewMode, setViewMode] = useState('strip') // 'strip' | 'month'
  const [weekOffset, setWeekOffset] = useState(0)

  // v189.2 BT-20: Auto-select first available date on mount so time slots show immediately
  // Only fires once when firstAvailable becomes known and no date is selected yet
  useEffect(() => {
    if (firstAvailable && !selectedDate && onSelectDate) {
      onSelectDate(firstAvailable)
    }
  }, [firstAvailable])

  // v214.4: If firstAvailable is beyond the default 14-day window, jump the
  // strip forward so the user sees the next available date right away.
  useEffect(() => {
    if (!firstAvailable) return
    const todayDate = new Date(today + 'T12:00:00')
    const firstAvailDate = new Date(firstAvailable + 'T12:00:00')
    const daysAhead = Math.floor((firstAvailDate - todayDate) / (1000 * 60 * 60 * 24))
    if (daysAhead >= 14) {
      // Round down to the nearest week boundary
      setWeekOffset(Math.floor(daysAhead / 7))
    }
  }, [firstAvailable, today])

  const firstDay = new Date(viewYear, viewMonth, 1)
  const lastDay = new Date(viewYear, viewMonth + 1, 0)
  const startDow = firstDay.getDay()
  // PROD-1330: the grid's month by name, through the calendar-day rule (local midnight on the 1st
  // formatted in Los Angeles was the previous month for every viewer east of LA).
  const monthLabel = formatCalendarMonth(viewYear, viewMonth, { month: 'long', year: 'numeric' }, appLocale(language))

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1) }
    else setViewMonth(viewMonth - 1)
  }
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1) }
    else setViewMonth(viewMonth + 1)
  }

  const isInSuggestedRange = (dateStr) => {
    if (!suggestedRange) return false
    return dateStr >= suggestedRange.start && dateStr <= suggestedRange.end
  }

  // Build calendar days (full month grid, used in 'month' view mode)
  const calendarDays = []
  for (let i = 0; i < startDow; i++) calendarDays.push(null) // padding
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const dateStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    calendarDays.push(dateStr)
  }

  // v214.4: Build the 14-day strip starting from today + weekOffset*7
  const stripDays = useMemo(() => {
    const start = new Date(today + 'T12:00:00')
    start.setDate(start.getDate() + weekOffset * 7)
    const days = []
    for (let i = 0; i < 14; i++) {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      days.push({ dateStr, dow: d.getDay() })
    }
    return days
  }, [today, weekOffset])

  // Strip header label — shows the month(s) covered by the visible 14 days
  const stripLabel = useMemo(() => {
    if (stripDays.length === 0) return ''
    // PROD-1330: the date-only days by name through the calendar-day rule (dateUtils).
    return calendarStripLabel(stripDays[0].dateStr, stripDays[stripDays.length - 1].dateStr, appLocale(language))
  }, [stripDays, language])

  const prevWeek = () => {
    // Don't allow going back past today
    if (weekOffset > 0) setWeekOffset(weekOffset - 1)
  }
  const nextWeek = () => setWeekOffset(weekOffset + 1)

  const formatTime = (t) => {
    // Pass through if already formatted (e.g., "9:00 AM")
    if (t.includes('AM') || t.includes('PM')) return t
    // Convert 24h to 12h
    const [h, m] = t.split(':').map(Number)
    const ampm = h >= 12 ? 'PM' : 'AM'
    const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h
    return `${h12}:${String(m).padStart(2, '0')} ${ampm}`
  }

  // v214.4: Unified day-cell renderer used by both views. The strip variant
  // includes a small DOW label above the digit so each cell is self-describing
  // outside the 7-column header context.
  const renderDayCell = (dateStr, opts = {}) => {
    const { showDow = false, key } = opts
    const isAvail = availableSet.has(dateStr)
    const isBooked = bookedSet.has(dateStr)
    // v237.18.0: today is NOT past — same-day slots are bookable. Use strict less-than
    // so the current day stays selectable when it's in availableDates.
    const isPast = dateStr < today
    const isSel = selectedDate === dateStr
    const inRange = isInSuggestedRange(dateStr)
    const day = parseInt(dateStr.split('-')[2])
    const disabled = isPast || isBooked || !isAvail

    // TEST 3.16: one rule for every state (dayCellStyle above)
    const { bgColor, textColor, border, fontWeight, textDecoration } = dayCellStyle({ isSel, isBooked, isPast, isAvail, inRange })

    const dow = new Date(dateStr + 'T12:00:00').getDay()
    const dowLabel = DAYS[dow]
    // Subtle DOW label color — inherit selected-state white for legibility
    const dowColor = isSel ? 'rgba(255,255,255,0.85)' : (disabled ? '#cbd5e1' : '#94a3b8')

    return (
      <button key={key || dateStr} disabled={disabled}
        onClick={() => onSelectDate(dateStr)}
        className="aspect-square rounded-[10px] flex flex-col items-center justify-center transition-all"
        style={{ background: bgColor, color: textColor, border, fontWeight, textDecoration, cursor: disabled ? 'default' : 'pointer' }}>
        {showDow && (
          <span className="text-[10px] font-semibold leading-none mb-0.5" style={{ color: dowColor }}>{dowLabel}</span>
        )}
        <span style={{ fontSize: 20, lineHeight: 1 }}>{day}</span>
      </button>
    )
  }

  return (
    <div>
      {/* ── Calendar Grid ── */}
      <div className="mb-4">
        {viewMode === 'strip' ? (
          <>
            {/* v214.4: Two-week strip — default view. Week-by-week navigation;
                "View full month" toggles to the month grid for further search. */}
            <div className="flex items-center justify-between mb-2.5">
              <button onClick={prevWeek}
                disabled={weekOffset === 0}
                className="w-8 h-8 rounded-[10px] border-[1.5px] border-gray-200 bg-white flex items-center justify-center active:scale-95 transition-transform"
                style={{ opacity: weekOffset === 0 ? 0.4 : 1, cursor: weekOffset === 0 ? 'default' : 'pointer' }}>
                <ChevronLeft size={16} className="text-gray-500" />
              </button>
              <span className="text-[14px] font-semibold" style={{ color: NAVY }}>{stripLabel}</span>
              <button onClick={nextWeek}
                className="w-8 h-8 rounded-[10px] border-[1.5px] border-gray-200 bg-white flex items-center justify-center active:scale-95 transition-transform">
                <ChevronRight size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Day cells — 14 days in 2 rows of 7. DOW label sits above the
                digit inside each cell, replacing the usual top-row DOW header. */}
            <div className="grid grid-cols-7 gap-[2px]">
              {stripDays.map(({ dateStr }) => renderDayCell(dateStr, { showDow: true }))}
            </div>

            {/* View full month toggle */}
            <div className="flex justify-center mt-2.5">
              <button onClick={() => setViewMode('month')}
                className="text-[12px] font-semibold py-1 px-2 active:opacity-70 transition-opacity"
                style={{ color: BLUE }}>
                {t('calendar.viewFullMonth') || 'View full month'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-2.5">
              <button onClick={prevMonth}
                className="w-8 h-8 rounded-[10px] border-[1.5px] border-gray-200 bg-white flex items-center justify-center active:scale-95 transition-transform">
                <ChevronLeft size={16} className="text-gray-500" />
              </button>
              <span className="text-[14px] font-semibold" style={{ color: NAVY }}>{monthLabel}</span>
              <button onClick={nextMonth}
                className="w-8 h-8 rounded-[10px] border-[1.5px] border-gray-200 bg-white flex items-center justify-center active:scale-95 transition-transform">
                <ChevronRight size={16} className="text-gray-500" />
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-[2px] text-center mb-1">
              {DAYS.map(d => (
                <div key={d} className="text-[10px] font-semibold text-gray-400 py-1">{d}</div>
              ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-[2px]">
              {calendarDays.map((dateStr, i) => {
                if (!dateStr) return <div key={`pad-${i}`} />
                return renderDayCell(dateStr, { key: dateStr })
              })}
            </div>

            {/* Back to two-week toggle */}
            <div className="flex justify-center mt-2.5">
              <button onClick={() => setViewMode('strip')}
                className="text-[12px] font-semibold py-1 px-2 active:opacity-70 transition-opacity"
                style={{ color: BLUE }}>
                {t('calendar.viewTwoWeeks') || 'Back to next 2 weeks'}
              </button>
            </div>
          </>
        )}

        {/* Legend — v214.3 (C-A-180): "Next available" chip removed; the
            calendar auto-selects the first available date on mount, so the
            outline treatment that label referred to never renders. */}
        <div className="flex gap-3 mt-2 px-0.5">
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm" data-legend-available style={AVAILABLE_SWATCH} />
            <span className="text-[9px] text-gray-400">{t('calendar.available')}</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2.5 h-2.5 rounded-sm bg-gray-100" />
            <span className="text-[9px] text-gray-400">{t('calendar.fullyBooked')}</span>
          </div>
          {suggestedRange && (
            <div className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-sm" data-legend-suggested style={SUGGESTED_SWATCH} />
              <span className="text-[9px] text-gray-400">{t('calendar.suggested')}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Time Slots ── */}
      {selectedDate && timeSlots.length > 0 && (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider mb-2 pb-1 inline-block"
            style={{ color: NAVY, borderBottom: `1.5px solid ${NAVY}` }}>{t('calendar.selectTime')}</p>
          <div className="grid grid-cols-3 gap-1.5">
            {timeSlots.map(t => {
              const isSel = selectedTime === t
              return (
                <button key={t} onClick={() => onSelectTime(t)}
                  className="py-2.5 rounded-xl border-[1.5px] text-[12px] font-semibold transition-all"
                  style={{
                    borderColor: isSel ? BLUE : '#E2E8F0',
                    background: isSel ? ICE : 'white',
                    color: isSel ? BLUE : '#64748b',
                  }}>
                  {formatTime(t)}
                  {timeSubLabels && timeSubLabels[t] ? (
                    <span className="block text-[10px] font-medium leading-tight mt-0.5 truncate px-1" data-slot-place-name="">{timeSubLabels[t]}</span>
                  ) : null}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {selectedDate && timeSlots.length === 0 && (
        <p className="text-[12px] text-gray-400 text-center py-3">{t('calendar.noAvailableTimesForThis')}</p>
      )}
    </div>
  )
}
